import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { EyeIcon, NoSymbolIcon, CheckCircleIcon, PlusIcon, UserIcon, XMarkIcon, PencilIcon } from '@heroicons/react/24/outline';
import { userAPI, parameterAPI, mutualisteAPI, formatDate, getStatusColor, getStatusLabel } from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';

// Helper pour obtenir le label du rôle
const getRoleLabel = (role) => {
  const labels = {
    'ADMIN': 'Administrateur',
    'AGENT': 'Agent',
    'SUPERVISEUR': 'Superviseur',
    'MUTUALISTE': 'Mutualiste'
  };
  return labels[role] || role;
};

// Helper pour obtenir la couleur du badge du rôle
const getRoleBadgeColor = (role) => {
  const colors = {
    'ADMIN': 'badge-danger',
    'AGENT': 'badge-info',
    'SUPERVISEUR': 'badge-warning',
    'MUTUALISTE': 'badge-success'
  };
  return colors[role] || 'badge-secondary';
};

const Users = () => {
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    role: '',
    page: 1,
    limit: 10
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    role: 'MUTUALISTE',
    mutualisteId: ''
  });
  const [mutualisteSearch, setMutualisteSearch] = useState('');
  const [showMutualisteDropdown, setShowMutualisteDropdown] = useState(false);
  
  const queryClient = useQueryClient();

  // Récupérer les utilisateurs
  const { data: usersData, isLoading, refetch } = useQuery(
    ['users', filters],
    () => userAPI.getUsers(filters),
    {
      keepPreviousData: true
    }
  );

  const users = usersData?.data?.users || [];
  const pagination = usersData?.data?.pagination || {};

  // Récupérer la liste des mutualistes pour le rattachement (avec recherche)
  const { data: mutualistesData, isLoading: mutualistesLoading, error: mutualistesError } = useQuery(
    ['mutualistes-search', mutualisteSearch],
    () => mutualisteAPI.searchMutualistes({ q: mutualisteSearch }),
    {
      enabled: showCreateModal && mutualisteSearch.length >= 2,
      retry: 3,
      onError: (error) => {
        console.error('Erreur recherche mutualistes:', error);
        toast.error('Erreur lors de la recherche des mutualistes');
      }
    }
  );

  // Mutation pour changer le mot de passe (utilisée pour la réinitialisation par un admin)
  const changePasswordMutation = useMutation(
    ({ userId, newPassword }) => userAPI.changePassword(userId, { newPassword }),
    {
      onSuccess: (_, variables) => {
        toast.success(`Mot de passe réinitialisé avec succès. Nouveau mot de passe : ${variables.newPassword}`);
        // Fermer le modal après réinitialisation pour plus de clarté
        setShowEditModal(false);
        setEditingUser(null);
        refetch();
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Erreur lors de la réinitialisation du mot de passe');
      }
    }
  );

  // Générer un mot de passe aléatoire sécurisé
  const generateSecurePassword = (length = 12) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const handleResetPassword = () => {
    if (!editingUser?.id) return;

    if (!window.confirm('Êtes-vous sûr de vouloir réinitialiser le mot de passe de cet utilisateur ?')) {
      return;
    }

    const newPassword = generateSecurePassword();

    changePasswordMutation.mutate({
      userId: editingUser.id,
      newPassword
    });
  };

  const mutualistes = mutualistesData?.data?.data || mutualistesData?.data || [];

  // Mutation pour créer un utilisateur
  const createUserMutation = useMutation(
    (userData) => userAPI.createUser(userData),
    {
      onSuccess: () => {
        toast.success('Utilisateur créé avec succès');
        setShowCreateModal(false);
        setNewUser({
          email: '',
          password: '',
          role: 'MUTUALISTE',
          mutualisteId: ''
        });
        refetch();
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || error.response?.data?.details || 'Erreur lors de la création');
      }
    }
  );

  // Mutation pour modifier un utilisateur
  const updateUserMutation = useMutation(
    ({ userId, userData }) => userAPI.updateUser(userId, userData),
    {
      onSuccess: () => {
        toast.success('Utilisateur modifié avec succès');
        setShowEditModal(false);
        setEditingUser(null);
        refetch();
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Erreur lors de la modification');
      }
    }
  );

  const handleStatusChange = async (userId, newStatus) => {
    const statusLabels = {
      ACTIVE: 'activer',
      SUSPENDED: 'suspendre',
      PENDING: 'mettre en attente'
    };

    if (window.confirm(`Êtes-vous sûr de vouloir ${statusLabels[newStatus]} cet utilisateur ?`)) {
      try {
        await userAPI.updateUserStatus(userId, newStatus);
        toast.success(`Utilisateur ${statusLabels[newStatus]} avec succès`);
        refetch();
      } catch (error) {
        toast.error('Erreur lors de la modification du statut');
      }
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filtering
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const handleCreateUser = (e) => {
    e.preventDefault();
    
    if (!newUser.email || !newUser.password) {
      toast.error('Email et mot de passe sont requis');
      return;
    }

    if (newUser.role === 'MUTUALISTE') {
      if (mutualistesLoading) {
        toast.error('Veuillez attendre le chargement des mutualistes');
        return;
      }
      
      if (mutualistesError) {
        toast.error('Erreur lors du chargement des mutualistes. Veuillez fermer et rouvrir le modal.');
        return;
      }
      
      if (!newUser.mutualisteId) {
        toast.error('Veuillez sélectionner un mutualiste');
        return;
      }
    }

    createUserMutation.mutate(newUser);
  };

  const handleNewUserChange = (field, value) => {
    setNewUser(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setNewUser({
      email: '',
      password: '',
      role: 'MUTUALISTE',
      mutualisteId: ''
    });
    setMutualisteSearch('');
    setShowMutualisteDropdown(false);
  };

  const handleOpenEditModal = (user) => {
    setEditingUser({
      id: user.id,
      email: user.email,
      role: user.role,
      statusId: user.statusId
    });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingUser(null);
  };

  const handleEditUserChange = (field, value) => {
    setEditingUser(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUpdateUser = (e) => {
    e.preventDefault();
    updateUserMutation.mutate({
      userId: editingUser.id,
      userData: {
        email: editingUser.email,
        role: editingUser.role,
        statusId: editingUser.statusId
      }
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-primary-900 mb-2">
              Gestion des utilisateurs
            </h1>
            <p className="text-gray-600">
              Gérez les comptes mutualistes et administrateurs
            </p>
          </div>
          <button 
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Nouvel utilisateur
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="form-label">Recherche</label>
            <input
              type="text"
              placeholder="Nom, email..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="form-input"
            />
          </div>
          <div>
            <label className="form-label">Statut</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="form-input"
            >
              <option value="">Tous les statuts</option>
              <option value="ACTIVE">Actif</option>
              <option value="SUSPENDED">Suspendu</option>
              <option value="PENDING">En attente</option>
            </select>
          </div>
          <div>
            <label className="form-label">Rôle</label>
            <select
              value={filters.role}
              onChange={(e) => handleFilterChange('role', e.target.value)}
              className="form-input"
            >
              <option value="">Tous les rôles</option>
              <option value="MUTUALISTE">Mutualiste</option>
              <option value="AGENT">Agent</option>
              <option value="SUPERVISEUR">Superviseur</option>
              <option value="ADMIN">Administrateur</option>
            </select>
          </div>
          <div>
            <label className="form-label">Date de création</label>
            <input
              type="date"
              className="form-input"
            />
          </div>
        </div>
      </div>

      {/* Statistiques rapides */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-card p-4">
          <div className="text-2xl font-bold text-primary-900">{pagination.total || 0}</div>
          <div className="text-sm text-gray-600">Total utilisateurs</div>
        </div>
        <div className="bg-white rounded-xl shadow-card p-4">
          <div className="text-2xl font-bold text-secondary-600">
            {users.filter(u => u.status === 'ACTIVE').length}
          </div>
          <div className="text-sm text-gray-600">Actifs</div>
        </div>
        <div className="bg-white rounded-xl shadow-card p-4">
          <div className="text-2xl font-bold text-accent-600">
            {users.filter(u => u.status === 'PENDING').length}
          </div>
          <div className="text-sm text-gray-600">En attente</div>
        </div>
        <div className="bg-white rounded-xl shadow-card p-4">
          <div className="text-2xl font-bold text-red-600">
            {users.filter(u => u.status === 'SUSPENDED').length}
          </div>
          <div className="text-sm text-gray-600">Suspendus</div>
        </div>
      </div>

      {/* Table des utilisateurs */}
      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        {users.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Aucun utilisateur trouvé</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utilisateur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rôle
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dernière connexion
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-primary-600 flex items-center justify-center">
                          {user.mutualiste ? (
                            <span className="text-sm font-medium text-white">
                              {user.mutualiste.prenoms?.[0]}{user.mutualiste.nom?.[0]}
                            </span>
                          ) : (
                            <UserIcon className="h-5 w-5 text-white" />
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.mutualiste 
                              ? `${user.mutualiste.prenoms} ${user.mutualiste.nom}`
                              : getRoleLabel(user.role)
                            }
                          </div>
                          <div className="text-sm text-gray-500">
                            {user.mutualiste?.mobile1 || 'Pas de téléphone'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`badge ${getRoleBadgeColor(user.role)}`}>
                        {getRoleLabel(user.role)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.lastLogin ? formatDate(user.lastLogin) : 'Jamais'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`badge badge-${getStatusColor(user.status?.libelle || user.status)}`}>
                        {getStatusLabel(user.status?.libelle || user.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => setSelectedUser(user)}
                          className="text-primary-600 hover:text-primary-900"
                          title="Voir les détails"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        
                        <button
                          onClick={() => handleOpenEditModal(user)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Modifier"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        
                        {user.status?.libelle === 'Actif(ve)' ? (
                          <button
                            onClick={() => handleStatusChange(user.id, 'SUSPENDED')}
                            className="text-red-600 hover:text-red-900"
                            title="Désactiver"
                          >
                            <NoSymbolIcon className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleStatusChange(user.id, 'ACTIVE')}
                            className="text-secondary-600 hover:text-secondary-900"
                            title="Activer"
                          >
                            <CheckCircleIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="bg-white rounded-xl shadow-card p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Affichage de {((pagination.page - 1) * pagination.limit) + 1} à {Math.min(pagination.page * pagination.limit, pagination.total)} sur {pagination.total} résultats
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="btn-secondary btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              
              {/* Pages */}
              {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                const pageNum = Math.max(1, pagination.page - 2) + i;
                if (pageNum > pagination.pages) return null;
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`btn-sm px-3 py-1 ${
                      pageNum === pagination.page 
                        ? 'bg-primary-600 text-white' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                className="btn-secondary btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal détails utilisateur */}
      {selectedUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setSelectedUser(null)} />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center mb-4">
                  <div className="h-12 w-12 rounded-full bg-primary-600 flex items-center justify-center">
                    {selectedUser.mutualiste ? (
                      <span className="text-lg font-medium text-white">
                        {selectedUser.mutualiste.prenoms?.[0]}{selectedUser.mutualiste.nom?.[0]}
                      </span>
                    ) : (
                      <UserIcon className="h-6 w-6 text-white" />
                    )}
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      {selectedUser.mutualiste 
                        ? `${selectedUser.mutualiste.prenoms} ${selectedUser.mutualiste.nom}`
                        : getRoleLabel(selectedUser.role)
                      }
                    </h3>
                    <p className="text-sm text-gray-500">{selectedUser.email}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Rôle</label>
                      <p className="text-sm text-gray-900">
                        {getRoleLabel(selectedUser.role)}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Statut</label>
                      <span className={`badge badge-${getStatusColor(selectedUser.status?.libelle || selectedUser.status)}`}>
                        {getStatusLabel(selectedUser.status?.libelle || selectedUser.status)}
                      </span>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Téléphone</label>
                      <p className="text-sm text-gray-900">
                        {selectedUser.mutualiste?.mobile1 || 'Non renseigné'}
                      </p>
                    </div>
                    {selectedUser.mutualiste && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Matricule</label>
                        <p className="text-sm text-gray-900">
                          {selectedUser.mutualiste.matricule}
                        </p>
                      </div>
                    )}
                    <div>
                      <label className="text-sm font-medium text-gray-500">Créé le</label>
                      <p className="text-sm text-gray-900">
                        {formatDate(selectedUser.createdAt)}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Créé par</label>
                      <p className="text-sm text-gray-900">
                        {selectedUser.createdBy_User 
                          ? selectedUser.createdBy_User.mutualiste 
                            ? `${selectedUser.createdBy_User.mutualiste.prenoms} ${selectedUser.createdBy_User.mutualiste.nom}`
                            : selectedUser.createdBy_User.email
                          : 'Système'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Dernière maj le</label>
                      <p className="text-sm text-gray-900">
                        {selectedUser.updatedAt ? formatDate(selectedUser.updatedAt) : 'Jamais'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Dernière maj par</label>
                      <p className="text-sm text-gray-900">
                        {selectedUser.updatedBy_User 
                          ? selectedUser.updatedBy_User.mutualiste 
                            ? `${selectedUser.updatedBy_User.mutualiste.prenoms} ${selectedUser.updatedBy_User.mutualiste.nom}`
                            : selectedUser.updatedBy_User.email
                          : 'Aucune'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Dernière connexion</label>
                      <p className="text-sm text-gray-900">
                        {selectedUser.lastLogin ? formatDate(selectedUser.lastLogin) : 'Jamais'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setSelectedUser(null)}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal création utilisateur */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseModal} />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleCreateUser}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      Créer un nouvel utilisateur
                    </h3>
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="form-label">Email *</label>
                      <input
                        type="email"
                        required
                        value={newUser.email}
                        onChange={(e) => handleNewUserChange('email', e.target.value)}
                        className="form-input"
                        placeholder="exemple@email.com"
                      />
                    </div>

                    <div>
                      <label className="form-label">Mot de passe *</label>
                      <input
                        type="password"
                        required
                        value={newUser.password}
                        onChange={(e) => handleNewUserChange('password', e.target.value)}
                        className="form-input"
                        placeholder="Mot de passe sécurisé"
                      />
                    </div>

                    <div>
                      <label className="form-label">Rôle *</label>
                      <select
                        value={newUser.role}
                        onChange={(e) => handleNewUserChange('role', e.target.value)}
                        className="form-input"
                      >
                        <option value="MUTUALISTE">Mutualiste</option>
                        <option value="AGENT">Agent</option>
                        <option value="SUPERVISEUR">Superviseur</option>
                        <option value="ADMIN">Administrateur</option>
                      </select>
                    </div>

                    {newUser.role === 'MUTUALISTE' && (
                      <div className="relative">
                        <label className="form-label">Mutualiste à rattacher *</label>
                        <input
                          type="text"
                          value={mutualisteSearch}
                          onChange={(e) => {
                            setMutualisteSearch(e.target.value);
                            setShowMutualisteDropdown(true);
                          }}
                          onFocus={() => setShowMutualisteDropdown(true)}
                          placeholder="Rechercher par nom ou matricule (min 2 caractères)..."
                          className="form-input"
                          required
                        />
                        
                        {showMutualisteDropdown && mutualisteSearch.length >= 2 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {mutualistesLoading && (
                              <div className="p-3 text-center text-gray-500">
                                <LoadingSpinner size="small" /> Recherche...
                              </div>
                            )}
                            {mutualistesError && (
                              <div className="p-3 text-center text-red-600">
                                Erreur de recherche
                              </div>
                            )}
                            {!mutualistesLoading && !mutualistesError && mutualistes.length === 0 && (
                              <div className="p-3 text-center text-yellow-600">
                                Aucun mutualiste trouvé
                              </div>
                            )}
                            {!mutualistesLoading && mutualistes.length > 0 && (
                              <ul>
                                {mutualistes.map((mutualiste) => (
                                  <li
                                    key={mutualiste.id}
                                    onClick={() => {
                                      handleNewUserChange('mutualisteId', mutualiste.id);
                                      setMutualisteSearch(`${mutualiste.prenoms} ${mutualiste.nom} (${mutualiste.matricule})`);
                                      setShowMutualisteDropdown(false);
                                    }}
                                    className="p-3 hover:bg-gray-100 cursor-pointer border-b border-gray-200 last:border-0"
                                  >
                                    <div className="font-medium">{mutualiste.prenoms} {mutualiste.nom}</div>
                                    <div className="text-sm text-gray-500">Matricule: {mutualiste.matricule}</div>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )}
                        {mutualisteSearch.length < 2 && (
                          <p className="text-sm text-gray-500 mt-1">
                            Saisissez au moins 2 caractères pour rechercher un mutualiste
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={createUserMutation.isLoading}
                    className="btn-primary ml-3"
                  >
                    {createUserMutation.isLoading ? 'Création...' : 'Créer'}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleCloseModal}
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal modification utilisateur */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseEditModal} />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleUpdateUser}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      Modifier l'utilisateur
                    </h3>
                    <button
                      type="button"
                      onClick={handleCloseEditModal}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="form-label">Email *</label>
                      <input
                        type="email"
                        required
                        value={editingUser.email}
                        onChange={(e) => handleEditUserChange('email', e.target.value)}
                        className="form-input"
                        placeholder="exemple@email.com"
                      />
                    </div>

                    <div>
                      <label className="form-label">Rôle *</label>
                      <select
                        value={editingUser.role}
                        onChange={(e) => handleEditUserChange('role', e.target.value)}
                        className="form-input"
                      >
                        <option value="MUTUALISTE">Mutualiste</option>
                        <option value="AGENT">Agent</option>
                        <option value="SUPERVISEUR">Superviseur</option>
                        <option value="ADMIN">Administrateur</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={updateUserMutation.isLoading}
                    className="btn-primary ml-3"
                  >
                    {updateUserMutation.isLoading ? 'Modification...' : 'Modifier'}
                  </button>
                  <button
                    type="button"
                    className="btn-warning ml-3"
                    onClick={handleResetPassword}
                    disabled={changePasswordMutation.isLoading}
                  >
                    {changePasswordMutation.isLoading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleCloseEditModal}
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
